import { REQUEST_STATUS } from 'spw-constants';
import _ from 'lodash';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { ICalculatorState, IChildWorksheetSpaceType, Scenario } from './types';
import {
  Actions,
  BUILD_NEW_SCENARIO_SUCCESS,
  CREATE_CHILD_WORKSHEET_SPACE_SUCCESS,
  CREATE_TOTAL_PROGRAM_SUCCESS,
  FETCH_ALL_CHILD_WORKSHEET_SPACES_SUCCESS,
  FETCH_CURRENT_WORKSHEET,
  FETCH_CURRENT_WORKSHEET_FAILED,
  FETCH_CURRENT_WORKSHEET_SUCCESS,
  FETCH_PROJECT_WORKSHEETS,
  FETCH_PROJECT_WORKSHEETS_FAILED,
  FETCH_PROJECT_WORKSHEETS_SUCCESS,
  FETCH_TPS_WORKSHEETS_SUCCESS,
  GET_PROJECT_SCENARIOS,
  GET_SCENARIOS_FAILED,
  GET_SCENARIOS_SUCCESS,
  POPULATE_CURRENT_WORKSHEET,
  SAVE_SCENARIO,
  SET_CALCULATOR_LOADING,
  SET_CALCULATOR_TYPE,
  SET_CHILD_WORKSHEET_SPACES_REQUEST_STATUS,
  SET_CURRENT_SCENARIO,
  SET_FACTOR,
  SET_GROWTH_YEARS,
  SET_HEADCOUNT_SEAT_COUNT_BASE,
  SET_HEADCOUNT_SEAT_COUNT_GROWTH_RATE,
  SET_IS_CALCULATOR_DIRTY,
  SET_IS_DELETE_CONFIRMATION_OPEN,
  SET_IS_EDITING_SAVED_SCENARIO,
  SET_IS_UNSAVED_DIALOG_OPEN,
  SET_LOADING_SCENARIOS,
  SET_PLANNING_FACTOR,
  SET_RSF_LOAD_FACTOR,
  SET_SCENARIO_DIALOG_STATUS,
  SET_SCENARIO_VALUE,
  SET_SELECTED_YEAR,
  SET_SUBTOTAL_BUILDING_MULTIPLIER,
  SET_SUBTOTAL_PROGRAM_MULTIPLIER,
  SET_TEMPORARY_SCENARIO,
  SET_TOTAL_PROGRAM_AVAILABLE_USF,
  SET_TOTAL_PROGRAM_DIALOG_STATUS,
  SET_UPDATE_SCENARIO_DIALOG,
  SET_WORKSHEET_NOTES_DIALOG_STATUS,
  SET_WORKSHEET_UPDATE_DIALOG_STATUS,
  TOTAL_PROGRAM_SCENARIOS_FETCH_FAILED,
  TOTAL_PROGRAM_SCENARIOS_FETCH_SUCCESS,
  UPDATE_BUILDING_SCENARIOS,
  SET_YEAR_HEADCOUNT,
  SAVE_SCENARIO_SUCCESS,
  SAVE_SCENARIO_FAILED,
  SET_SCENARIO_LIST_TYPE,
} from './actions';
import ProgramScenarioFactory from '../../../models/ProgramScenarioFactory';
import { CLEAN_UP_CURRENT_PROJECT } from '../projects/actions';
import ProgramScenario, {
  PlainProgramScenario,
} from '../../../models/Scenario';

const initialState: ICalculatorState = {
  currentScenario: null,
  currentCalculatorType: null,
  showScenarioDialog: false,
  showWorksheetUpdateDialog: false,
  showWorksheetNotesDialog: false,
  showTotalProgramDialog: false,
  areScenariosBeingRetrieved: false,
  scenariosRequestStatus: REQUEST_STATUS.NONE,
  totalProgramsRequestStatus: REQUEST_STATUS.NONE,
  worksheetRequestStatus: REQUEST_STATUS.NONE,
  worksheetsRequestStatus: REQUEST_STATUS.NONE,
  scenarios: [],
  isEditingSavedScenario: false,
  scenarioFloorId: null,
  worksheet: null,
  isLoading: false,
  isDeleteConfirmationOpen: false,
  temporaryScenario: null,
  duplicated: false,
  step: null,
  isUpdate: false,
  totalPrograms: [],
  childWorksheetSpacesRequestStatus: REQUEST_STATUS.NONE,
  childWorksheetSpaces: [],
  worksheets: [],
  tpsWorksheetsEnhanced: [],
  isCalculatorDirty: false,
  isUnsavedDialogOpen: false,
  isSavingScenario: false,
  scenarioListType: null,
};

const reducer = (
  state: ICalculatorState = initialState,
  action: Actions | { type: typeof CLEAN_UP_CURRENT_PROJECT }
) => {
  switch (action.type) {
    case SET_SELECTED_YEAR: {
      const updatedScenario = {
        ...ProgramScenario.toAPIScenario(state.currentScenario),
        targetYear: action.payload.selectedYear,
      };
      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario as Scenario
        ),
      };
    }
    case SET_HEADCOUNT_SEAT_COUNT_BASE: {
      const { headcountSeatCountBase } = action.payload;
      const updatedScenario = {
        ...ProgramScenario.toAPIScenario(state.currentScenario),
        headcountSeatCountBase,
      };

      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario as Scenario
        ),
      };
    }
    case SET_YEAR_HEADCOUNT: {
      const {
        headcount,
        headcountIndex,
        otherEntriesAreClean,
      } = action.payload;

      let growthEntries = [...state.currentScenario.growths?.[0].growthEntries];

      if (otherEntriesAreClean) {
        growthEntries = growthEntries.map((ge, i) => ({
          ...ge,
          value: i >= headcountIndex ? headcount : 0,
        }));
      } else {
        growthEntries[headcountIndex].value = headcount;
      }

      const s = {
        ...state.currentScenario,
        headcountSeatCountGrowthRate: null,
      } as PlainProgramScenario;

      s.growths[0].growthEntries = growthEntries;

      const updatedScenario = ProgramScenario.toAPIScenario(s);

      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario as Scenario
        ),
      };
    }

    case SET_HEADCOUNT_SEAT_COUNT_GROWTH_RATE: {
      const updatedScenario = ProgramScenario.toAPIScenario({
        ...state.currentScenario,
        headcountSeatCountGrowthRate:
          action.payload.headcountSeatCountGrowthRate,
      });

      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario as Scenario
        ),
      };
    }
    case BUILD_NEW_SCENARIO_SUCCESS:
      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          action.payload.scenario as Scenario
        ),
      };
    case SET_CALCULATOR_TYPE:
      return { ...state, currentCalculatorType: action.payload.calculatorType };
    case SET_SCENARIO_DIALOG_STATUS:
      return {
        ...state,
        showScenarioDialog: action.payload.status,
        duplicated: action.payload.duplicated,
        step: action.payload.step,
      };
    case SET_UPDATE_SCENARIO_DIALOG:
      return {
        ...state,
        isUpdate: action.payload.isUpdate,
      };
    case SET_WORKSHEET_UPDATE_DIALOG_STATUS:
      return {
        ...state,
        showWorksheetUpdateDialog: action.payload.status,
      };
    case SET_WORKSHEET_NOTES_DIALOG_STATUS:
      return {
        ...state,
        showWorksheetNotesDialog: action.payload.status,
      };
    case SET_TOTAL_PROGRAM_DIALOG_STATUS:
      return {
        ...state,
        showTotalProgramDialog: action.payload.status,
        isUpdate: action.payload.isUpdate,
      };
    case SET_FACTOR: {
      return { ...state, [action.payload.factor]: action.payload.value };
    }
    case SET_GROWTH_YEARS: {
      const updatedScenario = {
        ...ProgramScenario.toAPIScenario(state.currentScenario),
        growthYears: action.payload.growthYears,
      };
      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario as Scenario
        ),
      };
    }
    case SET_LOADING_SCENARIOS: {
      return { ...state, areScenariosBeingRetrieved: action.payload.flag };
    }
    case GET_PROJECT_SCENARIOS: {
      return {
        ...state,
        scenariosRequestStatus: REQUEST_STATUS.QUERYING,
        totalProgramsRequestStatus: REQUEST_STATUS.QUERYING,
      };
    }
    case GET_SCENARIOS_SUCCESS: {
      return {
        ...state,
        scenarios: action.payload.scenarios,
        scenariosRequestStatus: REQUEST_STATUS.SUCCESS,
      };
    }
    case GET_SCENARIOS_FAILED: {
      return {
        ...state,
        scenariosRequestStatus: REQUEST_STATUS.NONE,
        totalProgramsRequestStatus: REQUEST_STATUS.NONE,
      };
    }
    case UPDATE_BUILDING_SCENARIOS: {
      return {
        ...state,
        scenarios: action.payload.scenarios,
      };
    }
    case SET_CURRENT_SCENARIO: {
      return {
        ...state,
        currentScenario: action.payload.scenario,
      };
    }
    case SET_SCENARIO_VALUE: {
      const updatedScenario = {
        ...ProgramScenario.toAPIScenario(state.currentScenario),
        [action.payload.factor]: action.payload.value,
      };
      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario as Scenario
        ),
      };
    }

    case SET_IS_EDITING_SAVED_SCENARIO: {
      return { ...state, isEditingSavedScenario: action.payload.value };
    }
    case SET_SUBTOTAL_BUILDING_MULTIPLIER: {
      if (isTotalProgram(state.currentScenario)) return state;
      const updatedScenario = {
        ...ProgramScenario.toAPIScenario(state.currentScenario),
        subtotalBuildingMultiplier: action.payload.value,
      };
      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario as Scenario
        ).toPlainObject(),
      };
    }
    case SET_SUBTOTAL_PROGRAM_MULTIPLIER: {
      if (isTotalProgram(state.currentScenario)) return state;
      const updatedScenario = {
        ...ProgramScenario.toAPIScenario(state.currentScenario),
        subtotalProgramMultiplier: action.payload.value,
      };
      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario as Scenario
        ).toPlainObject(),
      };
    }
    case SET_RSF_LOAD_FACTOR: {
      if (isTotalProgram(state.currentScenario)) return state;
      const updatedScenario = {
        ...ProgramScenario.toAPIScenario(state.currentScenario),
        rsfLoadFactor: action.payload.value,
      };
      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario
        ).toPlainObject(),
      };
    }
    case SET_PLANNING_FACTOR: {
      if (isTotalProgram(state.currentScenario)) return state;
      const updatedScenario = {
        ...ProgramScenario.toAPIScenario(state.currentScenario),
        planningFactor: action.payload.value,
      };
      return {
        ...state,
        currentScenario: ProgramScenarioFactory.create(
          updatedScenario
        ).toPlainObject(),
      };
    }
    case SET_TOTAL_PROGRAM_AVAILABLE_USF: {
      return {
        ...state,
        currentScenario: {
          ...state.currentScenario,
          totalAvailableUSF: action.payload.value,
          totalAvailableRSF:
            action.payload.value *
            (1 +
              ProgramScenario.toAPIScenario(state.currentScenario)
                .rsfLoadFactor),
        },
      };
    }
    case POPULATE_CURRENT_WORKSHEET: {
      const { worksheet } = action.payload;
      return {
        ...state,
        isWorksheetLoading: false,
        worksheet: worksheet === undefined ? null : worksheet,
      };
    }
    case SET_CALCULATOR_LOADING: {
      return {
        ...state,
        isLoading: action.payload.isLoading,
      };
    }
    case SET_IS_DELETE_CONFIRMATION_OPEN: {
      return {
        ...state,
        isDeleteConfirmationOpen: action.payload.isOpen,
      };
    }
    case SET_TEMPORARY_SCENARIO: {
      return {
        ...state,
        temporaryScenario: action.payload.scenario,
      };
    }
    case CREATE_TOTAL_PROGRAM_SUCCESS: {
      return {
        ...state,
        totalPrograms: [action.payload.totalProgramEnhanced].concat(
          state.totalPrograms
        ),
      };
    }
    case TOTAL_PROGRAM_SCENARIOS_FETCH_SUCCESS: {
      return {
        ...state,
        totalPrograms: action.payload.totalPrograms,
        totalProgramsRequestStatus: REQUEST_STATUS.SUCCESS,
      };
    }
    case TOTAL_PROGRAM_SCENARIOS_FETCH_FAILED: {
      return {
        ...state,
        totalProgramsRequestStatus: REQUEST_STATUS.NONE,
      };
    }
    case FETCH_CURRENT_WORKSHEET: {
      return {
        ...state,
        worksheetRequestStatus: REQUEST_STATUS.QUERYING,
      };
    }
    case FETCH_CURRENT_WORKSHEET_SUCCESS: {
      return {
        ...state,
        worksheetRequestStatus: REQUEST_STATUS.SUCCESS,
      };
    }
    case FETCH_CURRENT_WORKSHEET_FAILED: {
      return {
        ...state,
        worksheetRequestStatus: REQUEST_STATUS.NONE,
      };
    }
    case FETCH_PROJECT_WORKSHEETS: {
      return {
        ...state,
        worksheetsRequestStatus: REQUEST_STATUS.QUERYING,
      };
    }
    case FETCH_PROJECT_WORKSHEETS_SUCCESS: {
      return {
        ...state,
        worksheets: action.payload.worksheets,
        worksheetsRequestStatus: REQUEST_STATUS.SUCCESS,
      };
    }
    case FETCH_PROJECT_WORKSHEETS_FAILED: {
      return {
        ...state,
        worksheetsRequestStatus: REQUEST_STATUS.NONE,
      };
    }
    case FETCH_TPS_WORKSHEETS_SUCCESS: {
      return {
        ...state,
        tpsWorksheetsEnhanced: action.payload.worksheets,
      };
    }
    case SET_CHILD_WORKSHEET_SPACES_REQUEST_STATUS: {
      return {
        ...state,
        childWorksheetSpacesRequestStatus: action.payload.status,
      };
    }
    case FETCH_ALL_CHILD_WORKSHEET_SPACES_SUCCESS: {
      return {
        ...state,
        childWorksheetSpaces: action.payload,
        childWorksheetSpacesRequestStatus: REQUEST_STATUS.SUCCESS,
      };
    }
    case CREATE_CHILD_WORKSHEET_SPACE_SUCCESS: {
      const { childWorksheetSpace } = action.payload;
      const { childWorksheetSpaces } = state;

      const newList = childWorksheetSpaces.slice();
      const index = _.findIndex(
        childWorksheetSpaces,
        (c) => c?.id === childWorksheetSpace?.id
      );

      if (index > -1) {
        newList.splice(index, 1, {
          ...childWorksheetSpaces[index],
          ...childWorksheetSpace,
        } as IChildWorksheetSpaceType);
      } else {
        const previousChildSpaceTypeIndex = childWorksheetSpaces.findLastIndex(
          (cwst) => cwst.rank < childWorksheetSpace.rank
        );
        const nextChildSpaceTypeIndex = childWorksheetSpaces.findIndex(
          (cwst) => cwst.rank > childWorksheetSpace.rank
        );

        if (previousChildSpaceTypeIndex > -1) {
          newList.splice(
            previousChildSpaceTypeIndex + 1,
            0,
            childWorksheetSpace as IChildWorksheetSpaceType
          );
        } else if (nextChildSpaceTypeIndex > -1) {
          newList.splice(
            nextChildSpaceTypeIndex,
            0,
            childWorksheetSpace as IChildWorksheetSpaceType
          );
        } else {
          newList.push(childWorksheetSpace as IChildWorksheetSpaceType);
        }
      }

      return {
        ...state,
        childWorksheetSpaces: newList,
      };
    }
    case SAVE_SCENARIO:
      return {
        ...state,
        isCalculatorDirty: false,
        isSavingScenario: true,
      };
    case SAVE_SCENARIO_SUCCESS:
    case SAVE_SCENARIO_FAILED:
      return {
        ...state,
        isSavingScenario: false,
      };
    case SET_IS_CALCULATOR_DIRTY: {
      return {
        ...state,
        isCalculatorDirty: action.payload.isDirty,
      };
    }
    case SET_IS_UNSAVED_DIALOG_OPEN: {
      return {
        ...state,
        isUnsavedDialogOpen: action.payload.isOpen,
      };
    }
    case CLEAN_UP_CURRENT_PROJECT: {
      return initialState;
    }

    case SET_SCENARIO_LIST_TYPE: {
      return {
        ...state,
        scenarioListType: action.payload.scenarioListType,
      };
    }

    default:
      return state;
  }
};

export default reducer;
